import React, { useState } from 'react';
import GuidanceModeSelector from '../Chat/GuidanceModeSelector';

/**
 * SpawnSessionDialog — launch a working session from a Compendium entry
 * (a To Do or a Snippet).
 *
 * Offers a session-type picker (Explore / Focus). Explore spawns directly;
 * Focus reveals the standard Focus-area picker — the SAME component the
 * new-session UI uses (GuidanceModeSelector -> FocusPicker), covering preset
 * areas and user-defined custom focuses. Focus is only offered for
 * story-scoped entries (project sessions have no Focus templates).
 *
 * onConfirm receives a payload:
 *   { session_name, guidance_mode, template, focus_target_name, custom_focus_id }
 */
export function SpawnSessionDialog({ capture, action, onConfirm, onCancel }) {
  const [sessionName, setSessionName] = useState(
    capture.content.length > 50
      ? capture.content.substring(0, 50) + '...'
      : capture.content
  );

  const isStoryScoped = !!capture.story_id;
  // Idea cards (series-planning-cards, was 'snippet'); tolerate legacy 'snippet'.
  const isSnippet = capture.entry_type === 'idea' || capture.entry_type === 'snippet';

  // 'explore' | 'focus'  (Focus only available for story-scoped entries)
  const suggestedFocus = capture.recommended_focus_template || null;
  const [sessionType, setSessionType] = useState(
    isStoryScoped && suggestedFocus ? 'focus' : 'explore'
  );
  const [focusTemplate, setFocusTemplate] = useState(suggestedFocus);
  const [focusTargetName, setFocusTargetName] = useState('');
  const [customFocus, setCustomFocus] = useState(null);

  const canSubmit =
    !!sessionName.trim() &&
    (sessionType === 'explore' ||
      (sessionType === 'focus' && (!!focusTemplate || !!customFocus)));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Mirror NewSessionDialog's payload construction exactly
    const payload = {
      session_name: sessionName.trim(),
      guidance_mode: sessionType,
      template: sessionType === 'focus' ? focusTemplate : 'open',
      focus_target_name: sessionType === 'focus' ? (focusTargetName || null) : null,
    };
    if (sessionType === 'focus' && focusTemplate === 'custom' && customFocus) {
      payload.custom_focus_id = customFocus.id;
    }
    onConfirm(payload);
  };

  const title = action === 'revisit'
    ? `Revisit ${isSnippet ? 'idea' : 'To Do'}`
    : `Start session from ${isSnippet ? 'idea' : 'To Do'}`;

  return (
    <div className="spawn-session-dialog__overlay" onClick={onCancel}>
      <div className="spawn-session-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="spawn-session-dialog__title">{title}</h3>

        <form onSubmit={handleSubmit}>
          <div className="spawn-session-dialog__field">
            <label className="spawn-session-dialog__label">Session name:</label>
            <input
              type="text"
              className="spawn-session-dialog__input"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Session type picker: Explore / Focus */}
          <div className="spawn-session-dialog__field">
            <label className="spawn-session-dialog__label">Session type:</label>
            <div className="spawn-session-dialog__type-buttons">
              <button
                type="button"
                className={`spawn-session-dialog__type-btn ${sessionType === 'explore' ? 'spawn-session-dialog__type-btn--active' : ''}`}
                onClick={() => setSessionType('explore')}
              >
                <span className="spawn-session-dialog__type-name">Explore</span>
                <span className="spawn-session-dialog__type-desc">Follow your curiosity</span>
              </button>
              {isStoryScoped && (
                <button
                  type="button"
                  className={`spawn-session-dialog__type-btn ${sessionType === 'focus' ? 'spawn-session-dialog__type-btn--active' : ''}`}
                  onClick={() => setSessionType('focus')}
                >
                  <span className="spawn-session-dialog__type-name">Focus</span>
                  <span className="spawn-session-dialog__type-desc">Build depth in an element</span>
                </button>
              )}
            </div>
          </div>

          {/* Focus-area picker — the SAME component used by the new-session UI */}
          {sessionType === 'focus' && isStoryScoped && (
            <div className="spawn-session-dialog__field">
              <GuidanceModeSelector
                mode="focus"
                onModeChange={() => {}}
                template={focusTemplate}
                onTemplateChange={setFocusTemplate}
                focusTargetName={focusTargetName}
                onFocusTargetNameChange={setFocusTargetName}
                existingLoglineSession={false}
                hideModePicker={true}
                storyId={capture.story_id}
                projectId={capture.project_id}
                customFocus={customFocus}
                onCustomFocusChange={setCustomFocus}
                useNewFocusPicker={true}
              />
            </div>
          )}

          <div className="spawn-session-dialog__field">
            <label className="spawn-session-dialog__label">From {isSnippet ? 'idea' : 'To Do'}:</label>
            <p className="spawn-session-dialog__capture-preview">{capture.content}</p>
          </div>

          <div className="spawn-session-dialog__actions">
            <button
              type="button"
              className="spawn-session-dialog__cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="spawn-session-dialog__submit"
              disabled={!canSubmit}
            >
              Start Session
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SpawnSessionDialog;
